import { TranslateExcelBusiness } from '@/coreDomainsModule/translateExcel';
import _ from 'lodash';
import {
  ExcelRow,
  ExcelTranslateError,
  ExcelRegisteredFileInfo,
  IExcelInfo,
  ExcelTranslateErrorCode,
} from '../../../coreDomainsModule/translateExcel';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { IUnmanagedBusiness } from '@/coreDomainsModule/entities/unmanagedBusiness';
import { UnmanagedBusinessFactory } from '@/coreDomainsModule/factories/UnmanagedBusinessFactory';
import { UnmanagedBusinessService } from '@/coreDomainsModule/services/unmanagedBusinessService';
import { createBusinessId } from '@/businessRepository/idUtils';

/**
 * エラー情報の登録を行う
 * エラー対象のファイルは配列で渡す(取引重複のように複数ファイルにまたがってエラー発生している場合があるため)
 * @param errorObj Excelに転記する時のエラー
 * @param excelInfos エラーログ等の為の追加情報またはエクセルデータ一行をkey-valueに変換した情報
 * @param businessForUseLog IBusinessをラップしたエクセル転記用の情報
 * @param translateBusinessId
 * @param errCode Excelに転記する時のエラーの一覧(エラーコード)
 * @param registeredExcelInfo 登録済みのファイル情報(ファイル名、シート名、セル番号) 取引重複時など、読み取り済みのファイル情報を保持するための情報
 * @param unmanagedBusinessArray 未転記の取引情報を保持するための配列
 */
export const registerErrorInformation = (
  errorObj: ExcelTranslateError[],
  excelInfos: IExcelInfo | ExcelRow,
  businessForUseLog: TranslateExcelBusiness,
  translateBusinessId: string,
  errCode: ExcelTranslateErrorCode,
  registeredExcelInfo?: ExcelRegisteredFileInfo,
  unmanagedBusinessArray?: IUnmanagedBusiness[]
) => {
  switch (errCode) {
    case '転記先が存在しない':
      // 未転記の取引を登録する
      errorObj.push({
        key: translateBusinessId,
        errCode: errCode,
        errorFileName: {
          inputFileName: String(excelInfos.fileName),
        },
        preCellNumber: 'test2',
        errorSheetName: {
          inputSheetName: String(excelInfos.sheetNumber),
        },
        errorCellNumber: {
          inputCellNumber: String(excelInfos.cellNumber),
        },
        value: businessForUseLog,
      });

      // 未転記の取引情報を保持する
      // ImportFileTypeへの変換が失敗したら登録せずエラーログをコンソールに出力する
      const importFileType = UnmanagedBusinessService.covertToImportFileTypeFromExcelFileName(
        typeof excelInfos.fileName === 'string' ? excelInfos.fileName : ''
      );
      if (importFileType === null) {
        console.error('ImportFileTypeの変換に失敗しました。');
        return;
      }

      const inventory = businessForUseLog.mvpInventory?.map((inventory) => inventory.amount)[0];

      unmanagedBusinessArray?.push(
        UnmanagedBusinessFactory.create({
          businessNumber: businessForUseLog.businessNumber,
          businessName: businessForUseLog.businessName,
          supplierNumber: businessForUseLog.supplierNumber,
          supplierName: businessForUseLog.supplierName,
          salesDestinationNumber: businessForUseLog.salesDestinationNumber,
          salesDestinationName: businessForUseLog.salesDestinationName,
          warehouseNumber: businessForUseLog.warehouseNumber,
          warehouse: businessForUseLog.warehouse,
          package: businessForUseLog.package,
          unit: businessForUseLog.unit,
          leadTime: businessForUseLog.leadTime,
          leadTimeUnit: businessForUseLog.leadTimeUnit,
          moq: businessForUseLog.MOQ,
          importFileType: importFileType,
          inputDate: DateService.convertDateToIDate(new Date()),
          fileName: typeof excelInfos.fileName === 'string' ? excelInfos.fileName : '',
          announce: businessForUseLog.customerAnnounce,
          customerName: businessForUseLog.customerName,
          salesResults: businessForUseLog.salesResults,
          stdInventoryIndices: businessForUseLog.stdInventoryIndices,
          inventory,
          inventories: businessForUseLog.mvpInventory,
          // IArrival[]のamountのSumを取得する
          arrival: businessForUseLog.arrivals?.reduce((prev, current) => {
            return prev + current.amount;
          }, 0),
          arrivals: businessForUseLog.arrivals,
        })
      );
      break;
    case '取引情報が重複している':
      // 重複している取引を登録する
      errorObj.push({
        key: translateBusinessId,
        errCode: errCode,
        errorFileName: {
          inputFileName: String(excelInfos['商品CD']?.fileName ?? ''),
          registerFileName: registeredExcelInfo?.registeredFileName ?? '',
        },
        preCellNumber: 'test2',
        errorSheetName: {
          inputSheetName: excelInfos['商品CD']?.sheetName ?? '',
          registerSheetName: registeredExcelInfo?.registeredSheetName ?? '',
        },
        errorCellNumber: {
          inputCellNumber: excelInfos['商品CD']?.address ?? '',
          registerCellNumber: registeredExcelInfo?.registeredCellNumber ?? '',
        },
        value: businessForUseLog,
      });
    default:
      break;
  }
};
